import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './rootReducer';

export type SnackbarType = 'error' | 'success' | 'warning' | 'info';
export type PersistencyType = 'temporary' | 'persistent';

export type Snackbar = {
  id: string;
  message: React.ReactNode;
  type: SnackbarType;
  isOpen: boolean;
  durationInSeconds?: number;
  persistencyType?: PersistencyType;
};

export type SnackbarInitialState = {
  snackbars: Snackbar[];
  closedTemporarySnackbars: string[];
  closedPersistentSnackbars: string[];
};

export const initialState: SnackbarInitialState = {
  snackbars: [],
  closedTemporarySnackbars: [],
  closedPersistentSnackbars: [],
};

export const snackbarSlice = createSlice({
  name: 'snackbar',
  initialState,
  reducers: {
    addSnackbar: (
      state,
      action: PayloadAction<{ snackbar: Snackbar; currentUser?: string }>,
    ) => {
      const { snackbar, currentUser } = action.payload;
      if (
        !state.snackbars.some((s) => s.id === snackbar.id) &&
        !(
          snackbar.persistencyType === 'temporary' &&
          state.closedTemporarySnackbars.includes(
            `${snackbar.id}-${currentUser}`,
          )
        )
      ) {
        state.snackbars.push(snackbar);
      }
    },
    removeSnackbar: (
      state,
      action: PayloadAction<{ id: string; currentUser: string }>,
    ) => {
      const { id, currentUser } = action.payload;
      const snackbar = state.snackbars.find((s) => s.id === id);
      state.snackbars = state.snackbars.filter((s) => s.id !== id);

      if (snackbar && snackbar.persistencyType === 'temporary') {
        if (
          !state.closedTemporarySnackbars.includes(
            `${snackbar.id}-${currentUser}`,
          )
        ) {
          state.closedTemporarySnackbars.push(`${snackbar.id}-${currentUser}`);
          localStorage.setObject(
            'closedTemporarySnackbars',
            state.closedTemporarySnackbars,
          );
        }
      }

      if (snackbar && snackbar.persistencyType === 'persistent') {
        if (
          !state.closedPersistentSnackbars.includes(
            `${snackbar.id}-${currentUser}`,
          )
        ) {
          state.closedPersistentSnackbars.push(`${snackbar.id}-${currentUser}`);
          sessionStorage.setObject(
            'closedPersistentSnackbars',
            state.closedPersistentSnackbars,
          );
        }
      }
    },
    hydrateClosedTemporarySnackbars: (
      state,
      action: PayloadAction<string[]>,
    ) => {
      state.closedTemporarySnackbars = action.payload;
    },
    hydrateClosedPersistentSnackbars: (
      state,
      action: PayloadAction<string[]>,
    ) => {
      state.closedPersistentSnackbars = action.payload;
    },
  },
});

export const {
  addSnackbar,
  removeSnackbar,
  hydrateClosedTemporarySnackbars,
  hydrateClosedPersistentSnackbars,
} = snackbarSlice.actions;

export const snackbarSelectors = {
  selectSnackbars: (state: RootState) => state.snackbar.snackbars,
  selectClosedTemporarySnackbars: (state: RootState) =>
    state.snackbar.closedTemporarySnackbars,
  selectClosedPersistentSnackbars: (state: RootState) =>
    state.snackbar.closedPersistentSnackbars,
};

export default snackbarSlice.reducer;
